// ============================================================
// Chess Arena — Level Start Screen
// Opponent preview, ability booster selection, and BATTLE button
// ============================================================

import type { Screen } from '../screenManager';
import type { OpponentConfig } from '../../core/types';
import { playerState } from '../../core/playerState';
import gameConfig from '../../core/gameConfig';

export class LevelStartScreen implements Screen {
  readonly name = 'levelStart';
  private container!: HTMLElement;

  // Callbacks
  private onBattle: (() => void) | null = null;
  private onExit: (() => void) | null = null;

  // DOM refs
  private levelNumberEl!: HTMLElement;
  private portraitEl!: HTMLElement;
  private nameEl!: HTMLElement;
  private ratingEl!: HTMLElement;
  private battleBtn!: HTMLButtonElement;
  private exitBtn!: HTMLButtonElement;
  private loadingEl!: HTMLElement;

  setOnBattle(cb: () => void): void {
    this.onBattle = cb;
  }

  setOnExit(cb: () => void): void {
    this.onExit = cb;
  }

  init(container: HTMLElement): void {
    this.container = container;
    container.classList.add('level-start-screen');

    container.innerHTML = `
      <div class="ls-card">
        <button class="ls-exit-btn" data-ref="exitBtn">✕</button>
        <div class="ls-level-number" data-ref="levelNumber">Level 1</div>
        <div class="ls-opponent">
          <div class="ls-opponent-portrait" data-ref="portrait">\uD83E\uDD16</div>
          <div class="ls-opponent-name" data-ref="name">Opponent</div>
          <div class="ls-opponent-rating" data-ref="rating">
            \uD83C\uDFC6 <span>0</span>
          </div>
        </div>
        <div class="ls-boosters" data-ref="boosters"></div>
        <div class="ls-loading" data-ref="loading">
          <div class="ls-spinner"></div>
          <span>Preparing battlefield...</span>
        </div>
        <button class="ca-btn ca-btn-green ca-btn-lg" data-ref="battleBtn" disabled>
          BATTLE
        </button>
      </div>
    `;

    this.levelNumberEl = container.querySelector('[data-ref="levelNumber"]')!;
    this.portraitEl = container.querySelector('[data-ref="portrait"]')!;
    this.nameEl = container.querySelector('[data-ref="name"]')!;
    this.ratingEl = container.querySelector('[data-ref="rating"] span')!;
    this.battleBtn = container.querySelector('[data-ref="battleBtn"]')! as HTMLButtonElement;
    this.exitBtn = container.querySelector('[data-ref="exitBtn"]')! as HTMLButtonElement;
    this.loadingEl = container.querySelector('[data-ref="loading"]')!;

    this.battleBtn.addEventListener('click', () => {
      if (!this.battleBtn.disabled) {
        this.onBattle?.();
      }
    });

    this.exitBtn.addEventListener('click', () => {
      this.onExit?.();
    });
  }

  show(data?: Record<string, unknown>): void {
    const level = (data?.level as number) ?? 1;
    const opponent = data?.opponent as OpponentConfig | undefined;

    this.levelNumberEl.textContent = `Level ${level}`;

    if (opponent) {
      this.portraitEl.textContent = opponent.portrait;
      this.nameEl.textContent = opponent.name;
      this.ratingEl.textContent = String(opponent.rating);
    }

    // Start in loading state
    this.battleBtn.disabled = true;
    this.loadingEl.style.display = '';

    // Render booster selection
    this.renderBoosters();

    // If assets are already loaded (or no loading needed), enable immediately
    if (data?.assetsReady) {
      this.setReady();
    }
  }

  // ------------------------------------------------------------------
  // Booster selection (Royal Match style — toggleable icons)
  // ------------------------------------------------------------------

  private renderBoosters(): void {
    const boostersEl = this.container.querySelector('[data-ref="boosters"]');
    if (!boostersEl) return;

    const abilityCfg = gameConfig.isInitialized ? gameConfig.getAbilityConfig() : null;
    const unlocked = playerState.abilitiesUnlocked;

    if (!abilityCfg || unlocked.length === 0) {
      boostersEl.innerHTML = '';
      return;
    }

    // Determine max equip slots
    const currentArena = playerState.arena;
    const baseSlots = abilityCfg.slots.initialSlots;
    const extraSlots = abilityCfg.slots.slotUnlocks.filter(
      s => currentArena >= s.arenaRequired,
    ).length;
    const maxSlots = baseSlots + extraSlots;

    const equipped = playerState.equippedAbilities;

    const icons = unlocked.map(id => {
      const cfg = abilityCfg.abilities[id];
      if (!cfg) return '';
      const charges = playerState.getCharges(id);
      const isSelected = equipped.includes(id);
      const noCharges = charges === 0;

      return `
        <div class="ls-booster ${isSelected ? 'ls-booster--selected' : ''} ${noCharges ? 'ls-booster--empty' : ''}"
             data-ability-id="${id}" title="${cfg.displayName}: ${cfg.description}">
          <div class="ls-booster-circle">
            <img src="assets/ui/${cfg.iconKey}.svg" alt="${cfg.displayName}" class="ls-booster-icon">
          </div>
          <div class="ls-booster-badge">${charges}</div>
          ${isSelected ? '<div class="ls-booster-check">✓</div>' : ''}
        </div>
      `;
    }).join('');

    boostersEl.innerHTML = `
      <div class="ls-boosters-row">${icons}</div>
      <div class="ls-boosters-hint">${equipped.length}/${maxSlots} selected</div>
    `;

    // Click handlers — toggle select/deselect
    boostersEl.querySelectorAll('.ls-booster').forEach(el => {
      el.addEventListener('click', () => {
        const abilityId = el.getAttribute('data-ability-id');
        if (!abilityId) return;

        const charges = playerState.getCharges(abilityId);
        if (charges === 0) return; // can't select empty

        const current = [...playerState.equippedAbilities];

        if (current.includes(abilityId)) {
          // Deselect
          playerState.setEquippedAbilities(current.filter(a => a !== abilityId));
        } else {
          // Select — check slot limit
          if (current.length >= maxSlots) {
            // Replace the last one
            current[current.length - 1] = abilityId;
            playerState.setEquippedAbilities(current);
          } else {
            current.push(abilityId);
            playerState.setEquippedAbilities(current);
          }
        }

        // Re-render
        this.renderBoosters();
      });
    });
  }

  /** Call this when assets have finished loading to enable the BATTLE button. */
  setReady(): void {
    this.battleBtn.disabled = false;
    this.loadingEl.style.display = 'none';
  }

  hide(): void {
    // Reset state
    this.battleBtn.disabled = true;
    this.loadingEl.style.display = '';
  }

  update(): void {
    // No periodic updates needed
  }

  destroy(): void {
    this.container.innerHTML = '';
  }
}
